import { formatCount } from '../lib/storage';
import type { SourceConfig, TagKey } from '../lib/types';
import { TextField } from './Field';

interface Props {
  source: SourceConfig;
  onChange: (next: SourceConfig) => void;
  /** Dotted paths of duration fields that failed to parse. */
  invalidPaths: Set<string>;
  /** Series implied by the current tags and fields, shown as live feedback. */
  seriesCount: number;
  retainedSeriesCount: number;
}

export default function SourcePanel({
  source,
  onChange,
  invalidPaths,
  seriesCount,
  retainedSeriesCount,
}: Props) {
  const patch = (delta: Partial<SourceConfig>) => onChange({ ...source, ...delta });
  const patchTag = (index: number, delta: Partial<TagKey>) =>
    patch({ tagKeys: source.tagKeys.map((t, i) => (i === index ? { ...t, ...delta } : t)) });

  return (
    <section className="panel" aria-labelledby="source-heading">
      <header>
        <h2 id="source-heading">Source</h2>
        <span className="panel-note">{formatCount(seriesCount)} raw series</span>
      </header>
      <div className="panel-body">
        <div className="grid-2">
          <TextField
            label="Raw bucket"
            value={source.bucket}
            onChange={(bucket) => patch({ bucket })}
            testId="source-bucket"
          />
          <TextField label="Organisation" value={source.org} onChange={(org) => patch({ org })} />
        </div>
        <TextField
          label="Measurement"
          value={source.measurement}
          onChange={(measurement) => patch({ measurement })}
          testId="source-measurement"
        />
        <TextField
          label="Fields"
          hint="Comma-separated. Each field is a separate series per tag combination."
          value={source.fields.join(', ')}
          onChange={(value) =>
            patch({
              fields: value
                .split(',')
                .map((f) => f.trim())
                .filter((f) => f !== ''),
            })
          }
          testId="source-fields"
        />
        <div className="grid-2">
          <TextField
            label="Sample interval"
            hint="e.g. 10s"
            value={source.interval}
            invalid={invalidPaths.has('source.interval')}
            onChange={(interval) => patch({ interval })}
            testId="source-interval"
          />
          <TextField
            label="Raw retention"
            hint="0s keeps forever"
            value={source.retention}
            invalid={invalidPaths.has('source.retention')}
            onChange={(retention) => patch({ retention })}
            testId="source-retention"
          />
        </div>

        <fieldset className="agg-list" style={{ display: 'block', marginTop: 6 }}>
          <legend>Tag keys</legend>
          <p className="hint" style={{ marginTop: 0, marginBottom: 10 }}>
            Cardinality is the number of distinct values. Untick <em>keep</em> to drop the tag at
            the first rollup hop — series count is the product of everything you keep, so dropping
            one high-cardinality tag is usually worth more than every other optimisation combined.
          </p>
          {source.tagKeys.map((tag, i) => (
            <div className="tag-row" key={i}>
              <input
                type="text"
                value={tag.key}
                aria-label={`Tag key ${i + 1}`}
                onChange={(e) => patchTag(i, { key: e.target.value })}
              />
              <input
                type="number"
                min={1}
                value={tag.cardinality}
                aria-label={`Distinct values for ${tag.key || `tag ${i + 1}`}`}
                onChange={(e) =>
                  patchTag(i, { cardinality: Math.max(1, Number(e.target.value) || 1) })
                }
              />
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={tag.retained}
                  onChange={(e) => patchTag(i, { retained: e.target.checked })}
                />
                keep
              </label>
              <button
                type="button"
                className="ghost"
                aria-label={`Remove tag ${tag.key || i + 1}`}
                onClick={() => patch({ tagKeys: source.tagKeys.filter((_, j) => j !== i) })}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              patch({ tagKeys: [...source.tagKeys, { key: '', cardinality: 10, retained: true }] })
            }
          >
            Add tag key
          </button>
        </fieldset>

        <p className="hint" style={{ marginTop: 12 }}>
          {formatCount(seriesCount)} series in the raw bucket, {formatCount(retainedSeriesCount)}{' '}
          carried into the first rollup before aggregates multiply them.
        </p>
      </div>
    </section>
  );
}
